import { EAnimalTypes } from 'data/types/animals.types';

/**
 * PP-10057. Shared copy for the OPP-Managed Animal Types setting and the flows it restricts.
 *
 * The three types are named Pet / ESA / Service animal throughout, in that order.
 */

/** Figma order: Pet, ESA, SA. */
export const ANIMAL_TYPES_ORDER: EAnimalTypes[] = [EAnimalTypes.PET, EAnimalTypes.ESA, EAnimalTypes.SA];

export const animalTypeLabels: Record<EAnimalTypes, string> = {
  [EAnimalTypes.PET]: 'Pet',
  [EAnimalTypes.ESA]: 'ESA',
  [EAnimalTypes.SA]: 'Service animal',
};

/** Subject of the warning heading: "Pets are not managed by...". */
const animalTypePlurals: Record<EAnimalTypes, string> = {
  [EAnimalTypes.PET]: 'Pets',
  [EAnimalTypes.ESA]: 'ESAs',
  [EAnimalTypes.SA]: 'Service animals',
};

/** Attributive singular, so the body reads "Any pet applications", not "Any pets applications". */
const animalTypeApplicationNouns: Record<EAnimalTypes, string> = {
  [EAnimalTypes.PET]: 'pet',
  // Stays capitalised mid-sentence: it is an initialism, not a word.
  [EAnimalTypes.ESA]: 'ESA',
  [EAnimalTypes.SA]: 'service animal',
};

/** "an ESA", "a pet" — the article has to follow the sound, not the type. */
const animalTypeArticles: Record<EAnimalTypes, string> = {
  [EAnimalTypes.PET]: 'a',
  [EAnimalTypes.ESA]: 'an',
  [EAnimalTypes.SA]: 'a',
};

/**
 * Sits above the warning block in the editor, because without it the block reads as a warning aimed
 * at the admin rather than a preview of what the resident will be shown.
 */
export const notManagedTypeAlertLeadIn = (type: EAnimalTypes): string =>
  `Applicants and residents will be alerted with the message below if ${animalTypeArticles[type]} ` +
  `${animalTypeApplicationNouns[type]} application is submitted.`;

export const notManagedTypeAlertTitle = (type: EAnimalTypes): string =>
  `${animalTypePlurals[type]} are not managed by OurPetPolicy at this property`;

/**
 * Says what unchecking the type actually does, in the order it happens: the applications go first,
 * then the admin's wording follows them to the people who see the retraction.
 */
export const notManagedTypeAlertDescription = (type: EAnimalTypes): string =>
  `Any ${animalTypeApplicationNouns[type]} applications will be retracted immediately. The reasoning ` +
  `typed below will be attached to the retracted application and will be visible to applicants and residents.`;

export const NOT_MANAGED_ANIMAL_TYPE_HINT = 'Not managed by OurPetPolicy at this property';

/**
 * Marks a not-managed option inside an antd dropdown item, so the global dropdown styling can scope
 * itself to this feature. The item is an `overlay` portalled to the body by a ui-kit component that
 * whitelists its props, so there is no outer class to scope by instead.
 */
export const NOT_MANAGED_ANIMAL_TYPE_MARKER = 'not-managed-animal-type';

/** Not a ui-kit token — the grey that marks an animal type OurPetPolicy does not manage. */
export const NOT_MANAGED_ANIMAL_TYPE_COLOR = '#D9E0E8';

export const notManagedAnimalTypeWarning = (label: string): string =>
  `Unable to add an animal of ${label} type because it is not managed by OurPetPolicy at this property.`;

export const reapplyBlockedByAnimalTypeWarning = (label: string): string =>
  `${label} management is not available through OurPetPolicy at this property, so this application cannot be reapplied.`;
